<script setup>
import {onMounted, onUnmounted, ref, watch, nextTick} from "vue";
import {useChatStore} from "@/stores/chat.js";
import {persianDate} from "@/utils/persianDate.js";
import Icon from "@/components/Icon/index.vue";
import Button from "@/components/Button/index.vue";
import FilePreview from "@/views/chat/FilePreview1.vue";
import VueEasyLightbox from "vue-easy-lightbox";
import LazyImage from "@/views/chat/LazyImage.vue";
import Offer from "@/views/chat/Offer.vue";
import Order from "@/views/chat/Order.vue";
import Modal from "@/components/Modal/Modal.vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import Badge from "@/components/Badge/index.vue";
import LoadingMessages from "@/views/chat/LoadingMessages.vue";
import SetData from "@/views/chat/SetData.vue";
import {useFormattedNearestTime} from "@/composables/useFormattedNearestTime.js";
import {Textarea} from "primevue";
import IconCheck from "@/components/Icon/icon-check.vue";
import IconDoubleCheck from "@/components/Icon/icon-double-check.vue";
import {useUserStore} from "@/stores/user.js";
import {showMessage} from "@/utils/showMessage.js";
import {useUploadStore} from "@/stores/upload.js";

const {getFormattedDate} = useFormattedNearestTime()

const isShowSetDataModal = ref(false);
const ordersStore = useMainOrdersStore();
const userStore = useUserStore()
const uploadStore = useUploadStore()
let interval = null
const cleared = ref(false)
const chatStore = useChatStore();
const messageContainer = ref();
let isScrolledToBottom = ref(false);
//const shouldScrollAfterMyMessage = ref(false)
const newMessage = ref("");
const orderSummary = ref({
  show: false,
  order: {},
  type: ''
})
const modal = ref(null)
const FOUR_HOURS = 4 * 60 * 60 * 1000;

// Lightbox visibility and active image index
const visible = ref(false);
const activeIndex = ref(0);
const openLightbox = (imageUrl) => {
  activeIndex.value = chatStore.get_images_in_chat.indexOf(imageUrl);
  visible.value = true;
};
const TIME_WINDOW = 60000;
const SCROLL_THRESHOLD = 40
const fetchNewMessages = async () => {
  if (cleared.value) return;
  if (
      chatStore.chatData?.messages &&
      chatStore.chatData.messages[0]?.id &&
      !chatStore.isGettingNewMessage
  ) {
    const oldLength = chatStore.chatData.messages?.length || 0;
    await chatStore.action_get_chat_after_messageID();
    const currentLength = chatStore.chatData.messages?.length || 0;
    const pendingMessages = chatStore.get_pendingMessagesByChannel;
    if (currentLength > oldLength && pendingMessages.length > 0) {
      const addedCount = currentLength - oldLength;
      // Assuming unshift (newest first), new messages at the beginning
      const newMessages = chatStore.chatData.messages.slice(0, addedCount);
      const ownNews = newMessages.filter(msg =>
          (msg.type === 'message' || msg.type === 'file') &&
          chatStore.chatData.contact !== msg.from
      );
      if (ownNews.length > 0) {
        const usedReals = new Set();
        // Sort pendings oldest first
        const sortedPendings = [...pendingMessages]
            .filter(p => (p.type === 'message' || p.type === 'file') && !p.failed)
            .sort((a, b) => a.time.getTime() - b.time.getTime());
        // Sort ownNews oldest first
        const sortedOwnNews = [...ownNews]
            .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
        for (const pending of sortedPendings) {
          let bestMatch = null;
          let minDiff = Infinity;

          for (const real of sortedOwnNews) {
            if (usedReals.has(real.id)) continue;

            // ✅ Different comparison for text and file
            if (pending.type === 'message' && real.type === 'message') {
              const diff = Math.abs(pending.time.getTime() - new Date(real.time).getTime());
              if (diff < TIME_WINDOW && pending.message === real.message && diff < minDiff) {
                minDiff = diff;
                bestMatch = real;
              }
            }

            if (pending.type === 'file' && real.type === 'file') {
              // Compare files by filename.
              const pendingFileName = pending.file?.name || '';
              const realFileName = real.file?.name || '';
              const diff = Math.abs(pending.time.getTime() - new Date(real.time).getTime());
              if (diff < TIME_WINDOW && pendingFileName === realFileName && diff < minDiff) {
                minDiff = diff;
                bestMatch = real;
              }
            }
          }

          if (bestMatch) {
            // Update pending
            const pendIndex = pendingMessages.findIndex(p => p.id === pending.id);
            if (pendIndex > -1) {
              const pend = pendingMessages[pendIndex];
              pend.id = bestMatch.id;
              pend.time = new Date(bestMatch.time);
              pend.pending = false;
              pend.failed = false;
            }

            usedReals.add(bestMatch.id);
            // Remove the matched real from messages
            const msgIndex = pendingMessages.findIndex(m => m.id === bestMatch.id);
            if (msgIndex > -1) {
              pendingMessages.splice(msgIndex, 1);
            }
          }
        }
      }
    }
    if (currentLength > oldLength) {
      await nextTick()
      const container = messageContainer.value
      if (container) {
        const distanceFromBottom = container.scrollTop !== 0 ? (Math.abs(container.scrollTop)) - SCROLL_THRESHOLD : 0
        if (distanceFromBottom < SCROLL_THRESHOLD) {
          await scrollToBottom()
        }
      }
      //shouldScrollAfterMyMessage.value = false
    }
  }
  if (!cleared.value) {
    interval = setTimeout(fetchNewMessages, 2000);
  }
};

onMounted(() => {
  chatStore.action_get_chat();
  fetchNewMessages();
})

onUnmounted(() => {
  //console.log('unmounted')
  if (interval) {
    clearInterval(interval)
    cleared.value = true
  }
})

watch(() => chatStore.selectedChat, (newValue, oldValue) => {
  chatStore.action_get_chat();
  newMessage.value = "";
}, {deep: true});

const scrollToBottom = async () => {
  await nextTick();
  const container = messageContainer.value;
  if (!container) return;

  container.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const sendMessage = async () => {
  if (!newMessage.value.trim()) return;

  const tempMessage = {
    id: Date.now() + Math.random(),
    message: newMessage.value,
    time: new Date(),
    type: 'message',
    from: chatStore.chatData.store.split(' USD')[0] + " - " + userStore.getName,
    pending: true,
    failed: false
  };
  chatStore.addPendingMessage1(tempMessage)
  const sentPending = tempMessage;

  try {
    await chatStore.action_send_message({
      message: newMessage.value
    });
    // NEW: Trigger fetch to get real message quickly; matching will remove pending
    //await fetchNewMessages();
  } catch (error) {
    sentPending.pending = false;
    sentPending.failed = true;
  }

  await nextTick()
  await scrollToBottom()
  newMessage.value = "";
};

async function onChange(e) {
  const file = e.target.files[0]
  chatStore.selectedFile = {
    file,
    url: URL.createObjectURL(file),
  }
  chatStore.activeFilePreview = true
}

function extractTextWithLinks(text) {
  const urlRegex = /(https?:\/\/[^\s]+)/g; // Match all links
  const parts = text.split(urlRegex); // Split text by links

  return parts.map((part) => ({
    type: urlRegex.test(part) ? "link" : "text",
    value: part,
  }));
}

async function openOrderSummary(emittedValue) {
  orderSummary.value.show = true
  orderSummary.value.type = emittedValue.orderType
  orderSummary.value.order = {}
  orderSummary.value.order = await ordersStore.action_get_order(emittedValue.orderType, emittedValue.orderID)
}

function closeModal(emittedValue) {
  if (!emittedValue) {
    modal.value.closeModal()
  }
  orderSummary.value.show = false
}

const formatText = (text) => {
  return text?.replace(/\n/g, '<br>') || ''
}

const getBadgeClass = (orderType) => {
  const baseClass = "text-white !font-normal !text-xs bg-gray-600 !px-2";
  const bgClass = {
    Account: "!bg-[#2196F3]",
    Boost: "!bg-[#4CAF50]",
    Currency: "!bg-[#FF6000]",
    Item: "!bg-[#795548]",
    "Gift Card": "!bg-[#E91E63]",
  }[orderType] || "";

  return `${baseClass} ${bgClass}`;
};

function showSetDataModal() {
  isShowSetDataModal.value = true
}

function closeSetDataModal() {
  isShowSetDataModal.value = false
}

const handleEnter = (e) => {
  if (e.shiftKey) {
    e.preventDefault()
    newMessage.value += "\n"
    nextTick(() => {
      forceResize()
    })
  } else {
    sendMessage()
  }
}

// این تابع مشکل اسکرول رو حل می‌کنه
const forceResize = () => {
  nextTick(() => {
    const textarea = document.querySelector('.p-textarea')
    if (!textarea) return

    // فورس آپدیت scrollHeight
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 2 + 'px'

    // فورس اسکرول به پایین
    textarea.scrollTop = textarea.scrollHeight
  })
}

async function customizeChannel() {
  //isLoading.value = true
  const result = await chatStore.action_customizeChannel({
    "channel": chatStore.selectedChat.channel,
    "orderType": chatStore.chatData.orderType,
    "orderGame": chatStore.chatData.orderGame
  });
  if (result === true) {
    showMessage('Data changed', 'success');
    //isLoading.value = false
    //closeModal()
    await chatStore.action_get_chat();
  } else {
    showMessage(result, 'error');
    //isLoading.value = false;
  }
}

const retryFile = async (message) => {
  message.pending = true;
  message.failed = false;

  try {
    const fileBlob = await fetch(message.file.url).then(r => r.blob());
    const file = new File([fileBlob], message.file.name, {type: message.file.type});

    const result = await uploadStore.uploadFile({
      fileName: message.file.name,
      file
    }, {returnResponse: true});

    const isUploaded = result?.success === true ? true : false

    if (isUploaded) {
      await chatStore.action_send_file({fileName: result.data.file.name});
      chatStore.removePendingMessage(message.id);
    } else {
      message.pending = false;
      message.failed = true;
    }
  } catch (err) {
    message.pending = false;
    message.failed = true;
  }
};


watch(newMessage, () => {
  forceResize()
})

onMounted(() => {
  forceResize()
})
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <header class="border-b border-slate-100 dark:border-slate-700">
      <div class="flex p-4 items-center">
        <div class="flex-1">
          <div class="flex rtl:space-x-reverse gap-4">
            <span
                @click="chatStore.toggleMobileSidebar()"
                class="text-slate-900 dark:text-white cursor-pointer text-xl self-center lg:hidden"
            ><Icon icon="tabler:menu-3"
            /></span>
            <div class="flex flex-col gap-1 flex-1 text-start">
              <div v-if="!chatStore.loadingMessages"
                   class="flex flex-row gap-2 text-slate-800 dark:text-slate-300 text-sm font-medium items-center">
                <div class="flex-1 flex flex-row gap-2 divide-x divide-slate-600 items-center">
                  <span>{{ chatStore.chatData.contact }} </span>
                  <span class="flex flex-row gap-2 items-center pl-2 relative">
                    <span class="flex flex-row gap-2 items-center group">
                      <Badge :badge-class="getBadgeClass(chatStore.chatData.orderType)">
                        <span class="block overflow-hidden whitespace-nowrap text-ellipsis max-w-20">
                          {{ chatStore.chatData.orderType }}
                        </span>
                      </Badge>
                      <span class="text-xs text-slate-300">{{ chatStore.chatData.orderGame }}</span>
                      <span v-if="chatStore.chatData.customizedByID"
                            class="opacity-0 invisible scale-0 group-hover:opacity-100 group-hover:visible group-hover:scale-100 absolute top-full text-xs mt-2 transition-all duration-300 p-4 rounded-md bg-slate-800 border border-slate-600 z-10">
                        Set by {{
                          chatStore.chatData.customizedBy
                        }} on {{ getFormattedDate(chatStore.chatData.customizedTime) }}
                      </span>
                    </span>
                    <span @click="showSetDataModal" class="cursor-pointer">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                        <g fill="none"
                           stroke="currentColor"
                           stroke-linecap="round"
                           stroke-linejoin="round"
                           stroke-width="1.5">
                          <path
                              d="M9.533 11.15A1.82 1.82 0 0 0 9 12.438V15h2.578c.483 0 .947-.192 1.289-.534l7.6-7.604a1.82 1.82 0 0 0 0-2.577l-.751-.751a1.82 1.82 0 0 0-2.578 0z"/>
                          <path
                              d="M21 12c0 4.243 0 6.364-1.318 7.682S16.242 21 12 21s-6.364 0-7.682-1.318S3 16.242 3 12s0-6.364 1.318-7.682S7.758 3 12 3"/>
                        </g>
                      </svg>
                    </span>
                  </span>
                  <span
                      v-if="chatStore.chatData.isUrgent && (Date.now() - (chatStore.chatData.customizedTime || 0) > FOUR_HOURS)"
                      class="flex flex-row items-center text-xs gap-1 pl-2">
                    <span>Is this correct?</span>
                    <button @click="customizeChannel">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="#018461"
                              d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22"
                              opacity="0.3"/>
                        <path fill="#009f75"
                              d="m10 13.6l5.9-5.9q.275-.275.7-.275t.7.275t.275.7t-.275.7l-6.6 6.6q-.3.3-.7.3t-.7-.3l-2.6-2.6q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275z"/>
                      </svg>
                    </button>
                    <button @click="showSetDataModal">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="red"
                              d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22"
                              opacity="0.3"/>
                        <path fill="none" stroke="#ff6373" stroke-linecap="round" stroke-width="1.5"
                              d="m8.464 15.535l7.072-7.07m-7.072 0l7.072 7.07"/>
                      </svg>
                    </button>
                  </span>
                </div>
              </div>
              <span v-if="chatStore.chatData.isTyping"
                    class="block text-slate-500 dark:text-slate-300 text-xs font-normal">
                {{ chatStore.chatData.isTyping ? chatStore.chatData.contact + ' is typing ...' : '' }}
              </span>
            </div>
          </div>
        </div>
        <!--        <div class="flex-none flex md:space-x-3 space-x-1 items-center rtl:space-x-reverse">
                  <div @click="openinfo" class="msg-action-btn">
                    <Icon icon="heroicons-outline:dots-horizontal"/>
                  </div>
                </div>-->
      </div>
    </header>
    <LoadingMessages v-if="chatStore.loadingMessages"></LoadingMessages>
    <div v-else class="flex-1 overflow-hidden">
      <div ref="messageContainer"
           class="space-y-3 flex flex-col-reverse h-full overflow-y-auto custom-scrollbar overflow-x-hidden">
        <div v-for="(message, index) in chatStore.get_pendingMessagesByChannel" :key="message.id"
             class="block md:px-6 px-4 mt-3">
          <div class="flex space-x-2 items-start group rtl:space-x-reverse w-full justify-end">
            <template v-if="message.type === 'message'">
              <div class="max-w-[80%] lg:max-w-[75%] flex flex-col gap-1">
                <div
                    class="text-contrent p-3 dark:text-slate-300 text-sm font-normal rounded-2xl flex-1 whitespace-pre-wrap break-all bg-slate-100 dark:bg-slate-700 text-slate-600">
                  <span class="text-xs text-blue-400">
                    {{ message.from }}
                  </span>
                  <p style="word-break: break-word">
                    <template v-for="(part, partIndex) in extractTextWithLinks(message.message)" :key="partIndex">
                      <template v-if="part.type === 'text'">{{ part.value }}</template>
                      <a v-else :href="part.value" target="_blank" class="underline text-blue-400">
                        {{ part.value }}
                      </a>
                    </template>
                  </p>
                </div>
                <span class="font-normal text-xs text-slate-400 flex items-center gap-2">
                  {{ persianDate(message.time, 'HH:mm') }}
                  <template v-if="message.pending">
                    <div class="animate-spin h-2.5 w-2.5 border-2 border-slate-400 border-t-transparent rounded-full">

                    </div>
                  </template>
                  <template v-else-if="message.failed">
                    <button @click="retry(message)"
                            class="ml-2 p-0 bg-transparent border-none text-slate-400 hover:text-slate-600">
                      <Icon icon="heroicons-outline:arrow-path" class="w-3 h-3"/>
                    </button>
                  </template>
                </span>
              </div>
            </template>
            <template v-else-if="message.type === 'file' && message.file.type.startsWith('image')">
              <div class="max-w-[80%] lg:max-w-[50%]">
                <div
                    class="flex flex-col dark:text-slate-300 rounded-2xl flex-1 overflow-hidden"
                    :class="chatStore.chatData.contact === message.from ? 'bg-slate-300 dark:bg-slate-900 text-slate-800' : 'bg-slate-100 dark:bg-slate-700 text-slate-600'">
                  <span v-if="chatStore.chatData.contact !== message.from"
                        class="text-xs text-blue-400 p-3">
                    {{ message.from }}
                  </span>
                  <LazyImage :message="message"/>
                </div>
                <div class="font-normal text-xs text-slate-400 flex flex-row gap-1 mt-2 items-end">
                  <span>{{ persianDate(message.time, 'HH:mm') }}</span>
                  <template v-if="message.pending">
                    <div
                        class="animate-spin h-2.5 w-2.5 border-2 border-slate-400 border-t-transparent rounded-full"></div>
                  </template>
                  <template v-else-if="message.failed">
                    <button @click="retryFile(message)"
                            class="ml-2 text-slate-400 hover:text-slate-600">
                      <Icon icon="heroicons-outline:arrow-path" class="w-3 h-3"/>
                    </button>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div v-for="(messages, date) in chatStore.get_grouped_messages_by_day" :key="date"
             class="message-group relative space-y-3">
          <div class="sticky top-3 flex justify-center mb-3 z-[2]">
            <div class="bg-slate-600 border border-white rounded-full px-4 py-0.5 text-white">
              {{ date }}
            </div>
          </div>
          <div class="block md:px-6 px-4" v-for="(message, i) in messages" :key="message.id" ref="chatContainer">
            <div class="flex space-x-2 items-start group rtl:space-x-reverse"
                 :class="[message.from ? chatStore.chatData.contact !== message.from ? 'w-full justify-end' : '' : 'w-full justify-center']">
              <template v-if="message.type === 'message'">
                <div class="max-w-[80%] lg:max-w-[75%]"
                     :class="message.from ? 'max-w-[80%] lg:max-w-[75%]' : 'max-w-[60%] lg:max-w-[55%]'">
                  <div
                      class="text-contrent p-3 dark:text-slate-300 text-sm font-normal rounded-2xl flex-1 whitespace-pre-wrap break-all"
                      :class="message.from ? chatStore.chatData.contact === message.from ? 'bg-slate-300 dark:bg-slate-900 text-slate-800' : 'bg-slate-100 dark:bg-slate-700 text-slate-600' : 'bg-[#c0392b]'">
                    <span v-if="chatStore.chatData.contact !== message.from"
                          class="text-xs text-blue-400">
                      {{ message.fromAdmin ? `${message.from} - ${message.fromAdmin}` : message.from }}
                    </span>
                    <p style="word-break: break-word">
                      <template v-for="(part, partIndex) in extractTextWithLinks(message.message)" :key="partIndex">
                        <template v-if="part.type === 'text'">{{ part.value }}</template>
                        <a v-else :href="part.value" target="_blank" class="underline"
                           :class="chatStore.chatData.contact === message.from ? 'text-success-600' : 'text-blue-400'">
                          {{ part.value }}
                        </a>
                      </template>
                    </p>
                  </div>
                  <div class="font-normal text-xs text-slate-400 flex flex-row gap-1 mt-2 items-end">
                    <span>{{ persianDate(message.time, 'HH:mm') }}</span>
                    <template v-if="chatStore.chatData.contact !== message.from">
                      <IconDoubleCheck v-if="chatStore.chatData.lastRead >= message.time"/>
                      <IconCheck v-else-if="message.from"/>
                    </template>
                  </div>
                </div>
              </template>
              <template v-else-if="message.type === 'offer-id' || message.type === 'order-id'">
                <div class="max-w-[80%] lg:max-w-[50%]">
                  <Offer v-if="message.type === 'offer-id'" :message="message"/>
                  <Order v-else-if="message.type === 'order-id'" :message="message"
                         @openOrderSummary="openOrderSummary"/>
                  <div class="font-normal text-xs text-slate-400 flex flex-row gap-1 mt-2 items-end">
                    <span>{{ persianDate(message.time, 'HH:mm') }}</span>
                    <template v-if="chatStore.chatData.contact !== message.from">
                      <IconDoubleCheck v-if="chatStore.chatData.lastRead >= message.time"/>
                      <!--                      <p v-else>{{ message.time }} - {{ chatStore.chatData.lastRead }}</p>-->
                      <IconCheck v-else-if="message.from"/>
                    </template>
                  </div>
                </div>
              </template>
              <template v-else-if="message.type === 'file' && message.file.type.startsWith('image')">
                <div class="max-w-[80%] lg:max-w-[50%]">
                  <div
                      class="flex flex-col dark:text-slate-300 rounded-2xl flex-1 overflow-hidden"
                      :class="chatStore.chatData.contact === message.from ? 'bg-slate-300 dark:bg-slate-900 text-slate-800' : 'bg-slate-100 dark:bg-slate-700 text-slate-600'">
                    <span v-if="chatStore.chatData.contact !== message.from"
                          class="text-xs text-blue-400 p-3">
                      {{ message.fromAdmin ? `${message.from} - ${message.fromAdmin}` : message.from }}
                    </span>
                    <LazyImage :message="message" @openLightbox="openLightbox(message.file.url)"/>
                    <p v-if="message.message"
                       class="p-3 text-contrent text-sm font-normal whitespace-pre-wrap break-all">
                      {{ message.message }}</p>
                  </div>
                  <div class="font-normal text-xs text-slate-400 flex flex-row gap-1 mt-2 items-end">
                    <span>{{ persianDate(message.time, 'HH:mm') }}</span>
                    <template v-if="chatStore.chatData.contact !== message.from">
                      <IconDoubleCheck v-if="chatStore.chatData.lastRead >= message.time"/>
                      <!--                      <p v-else>{{ message.time }} - {{ chatStore.chatData.lastRead }}</p>-->
                      <IconCheck v-else-if="message.from"/>
                    </template>
                  </div>
                </div>
              </template>
              <template v-else-if="message.type === 'file' && !message.file.type.startsWith('image')">
                <div class="max-w-[80%] lg:max-w-[75%]">
                  <div
                      class="flex flex-col dark:text-slate-300 rounded-2xl flex-1 overflow-hidden p-3"
                      :class="chatStore.chatData.contact === message.from ? 'bg-slate-300 dark:bg-slate-900 text-slate-800' : 'bg-slate-100 dark:bg-slate-700 text-slate-600'">
                    <span v-if="chatStore.chatData.contact !== message.from"
                          class="text-xs text-blue-400">
                      {{ message.fromAdmin ? `${message.from} - ${message.fromAdmin}` : message.from }}
                    </span>
                    <div
                        class="text-contrent underline dark:text-slate-300 text-slate-800 text-sm font-normal rounded-2xl flex-1 whitespace-pre-wrap break-all">
                      <a :href="message.file.url" target="_blank" class="hover:text-blue-300"
                         style="word-break: break-word">{{ message.file.url }}</a>
                    </div>
                    <p v-if="message.message"
                       class="p-3 text-contrent text-sm font-normal whitespace-pre-wrap break-all">
                      {{ message.message }}</p>
                  </div>
                  <div class="font-normal text-xs text-slate-400 flex flex-row gap-1 mt-2 items-end">
                    <span>{{ persianDate(message.time, 'HH:mm') }}</span>
                    <template v-if="chatStore.chatData.contact !== message.from">
                      <IconDoubleCheck v-if="chatStore.chatData.lastRead >= message.time"/>
                      <!--                      <p v-else>{{ message.time }} - {{ chatStore.chatData.lastRead }}</p>-->
                      <IconCheck v-else-if="message.from"/>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <FilePreview @scrollToBottom="scrollToBottom"/>
      <VueEasyLightbox
          :visible="visible"
          :imgs="chatStore.get_images_in_chat"
          :index="activeIndex"
          @hide="
        activeIndex = null;
        visible = false;
      "/>
      <Modal
          :title="orderSummary.type + ' Order - ' + orderSummary.order?.game"
          labelClass="bg-black-100 dark:bg-slate-900 dark:text-slate-50 btn-sm"
          ref="modal"
          :has-button="false"
          :activeModal="orderSummary.show"
          @closeModal="orderSummary.show = false"
          size-class="max-w-4xl max-h-full"
          contentClass="h-full"
          centered
      >
        <div class="space-y-4 text-white text-sm">
          <div class="grid grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="col-span-2 flex flex-col gap-6">
              <div class="flex flex-row gap-6 justify-between items-start">
                <p class="flex-1 text-start w-full bg-slate-700 p-2 rounded-lg">
                  {{ orderSummary.order?.title ? orderSummary.order.title : '&nbsp' }}</p>
                <span class="flex gap-2 items-center badge bg-slate-800 dark:bg-slate-900 py-2 px-3 rounded-lg">
                  <span v-if="orderSummary.order?.state"
                        class="inline-flex h-[6px] w-[6px] bg-primary-500 ring-opacity-25 rounded-full ring-4"
                        :class="[orderSummary.order?.state === 'New' ? 'bg-secondary-500 ring-secondary-500' : '',
                    orderSummary.order?.state === 'Delivering' ? 'bg-warning-500 ring-warning-500' : '',
                    orderSummary.order?.state === 'Delivered' ? 'bg-primary-500 ring-primary-500' : '',
                    orderSummary.order?.state === 'Canceled' ? 'bg-danger-500 ring-danger-500' : '',
                    orderSummary.order?.state === 'Paid' ? 'bg-success-500 ring-success-500' : '']"></span>
                  <span>{{ orderSummary.order?.state ? orderSummary.order.state : '&nbsp' }}</span>
                </span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Store:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.store ? orderSummary.order.store : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Game:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.game ? orderSummary.order.game : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Price:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.price ? orderSummary.order.price : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Takers Cut:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.takersCut ? orderSummary.order.takersCut : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Tax:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.tax ? orderSummary.order.tax : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Commission Fee:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.commissionFee ? orderSummary.order.commissionFee : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Income:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.income ? orderSummary.order.income : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Withdrawal Fee:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.withdrawalFee ? orderSummary.order.withdrawalFee : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Customer:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.customer ? orderSummary.order.customer : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">UUID:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.uuid ? orderSummary.order.uuid : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">G2G SellID:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.g2gSellID ? orderSummary.order.g2gSellID : '&nbsp' }}
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 items-start">
                  <p class="text-xs">Takers:</p>
                  <div class="w-full bg-slate-700 p-2 rounded-lg">
                    {{ orderSummary.order?.takers ? orderSummary.order.takers : '&nbsp' }}
                  </div>
                </div>
              </div>
              <div class="flex flex-col gap-0.5">
                <p class="text-xs">Link:</p>
                <div class="bg-slate-700 p-2 rounded-lg">
                  <a :href="orderSummary.order?.link" class="text-blue-400"
                     :class="orderSummary.order?.link ? 'underline' : ''">
                    {{ orderSummary.order?.link ? orderSummary.order.link : '&nbsp' }}
                  </a>
                </div>
              </div>
              <div class="flex flex-col gap-0.5">
                <p class="text-xs">Notes:</p>
                <div class="bg-slate-700 p-2 rounded-lg">
                  {{ orderSummary.order?.notes ? orderSummary.order?.notes : '&nbsp' }}
                </div>
              </div>
            </div>
            <div class="col-span-2 lg:col-span-1 flex flex-col gap-0.5">
              <p class="text-xs">Logs:</p>
              <p class="bg-slate-700 p-2 rounded-lg" v-html="formatText(orderSummary.order?.logs)"></p>
            </div>
          </div>
        </div>
        <template v-slot:footer>
          <Button
              :text="$t('message.close')"
              btnClass="btn-outline-dark py-1"
              @click="()=>{closeModal();}"
          />
          <!--          <button type="submit" @click="isEdit ? onEdit() : onSubmit()"
                            class="btn btn-dark text-center flex items-center justify-center gap-3">
                      {{ isEdit ? $t('message.edit') : $t('message.add') }}
                      <Icon v-show="loading" icon="eos-icons:bubble-loading" height="24"/>
                    </button>-->
        </template>
      </Modal>
      <SetData v-if="isShowSetDataModal" @closeModal="closeSetDataModal"/>
    </div>
    <footer
        class="md:px-4 px-2 flex flex-row items-end gap-2 border-t md:py-4 py-2 border-slate-100 dark:border-slate-700">
      <div class="flex-none flex md:space-x-3 space-x-1 rtl:space-x-reverse">
        <div
            class="h-8 w-8 cursor-pointer bg-slate-100 dark:bg-slate-900 dark:text-slate-400 flex flex-col justify-center items-center text-xl rounded-full">
          <label for="file" class="cursor-pointer">
            <input id="file" ref="fileInput" type="file" class="hidden w-full" @change="onChange($event)">
            <Icon icon="heroicons-outline:link"/>
          </label>
        </div>
      </div>
      <div class="w-full h-full overflow-hidden flex items-center">
          <Textarea
              v-model="newMessage"
              rows="1"
              :pt="{
                root: {
                  class: 'w-full max-h-48 overflow-y-auto scrollbar-thin !py-0',
                  style: 'resize: none;'
                }
              }"
              class="!border-transparent !bg-transparent"
              placeholder="Type your message ..."
              @keydown.enter.exact.prevent="handleEnter"
              @keydown.shift.enter.prevent="handleEnter"
          />
      </div>
      <div class="flex-none">
        <button
            type="button"
            @click="sendMessage"
            class="h-8 w-8 bg-slate-900 text-white flex flex-col justify-center items-center text-lg rounded-full"
        >
          <Icon
              icon="heroicons-outline:paper-airplane"
              class="transform rotate-[60deg]"
          />
        </button>
      </div>
    </footer>
  </div>
</template>

<style scoped lang="scss">
.msg-height {
  height: calc(100% - 0px);
}

.parent-height {
  height: calc(100% - 200px);
}

.msg-action-btn {
  @apply md:h-8 md:w-8 h-6 w-6 cursor-pointer bg-slate-100 dark:bg-slate-900 dark:text-slate-400 text-slate-900 flex flex-col justify-center items-center md:text-xl text-sm rounded-full;
}


.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #51607a;
  border-radius: 8px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #7283a6;
}

/* up and down buttons */
.scrollbar-thin::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}

/* اطمینان بیشتر: حالت‌های قبل و بعد از thumb */
.scrollbar-thin::-webkit-scrollbar-button:decrement,
.scrollbar-thin::-webkit-scrollbar-button:increment,
.scrollbar-thin::-webkit-scrollbar-button:vertical:decrement,
.scrollbar-thin::-webkit-scrollbar-button:vertical:increment {
  display: none;
  width: 0;
  height: 0;
}

</style>